import React, { useEffect, useState } from "react";
import api from "@/api";
import { toast } from "sonner";
import { Target, Save, Loader2, ExternalLink } from "lucide-react";

const FIELDS = [
  {
    key: "fb_pixel_id",
    label: "Facebook Pixel ID",
    placeholder: "e.g. 123456789012345",
    help: "Find this in Meta Events Manager → your pixel → Settings.",
    docs: "https://www.facebook.com/business/help/952192354843755",
  },
  {
    key: "ga4_id",
    label: "Google Analytics 4 (GA4)",
    placeholder: "G-XXXXXXXXXX",
    help: "From Google Analytics → Admin → Data streams → Measurement ID.",
    docs: "https://support.google.com/analytics/answer/9304153",
  },
  {
    key: "google_ads_id",
    label: "Google Ads Conversion ID",
    placeholder: "AW-XXXXXXXXX",
    help: "Optional — for Google Ads conversion tracking. Acts independently of GA4.",
    docs: "https://support.google.com/google-ads/answer/6095821",
  },
  {
    key: "gtm_id",
    label: "Google Tag Manager (GTM)",
    placeholder: "GTM-XXXXXXX",
    help: "If set, you can manage all other pixels through GTM instead — works alongside the IDs above.",
    docs: "https://support.google.com/tagmanager/answer/6103696",
  },
];

export default function PixelsPanel() {
  const [vals, setVals] = useState({ fb_pixel_id: "", ga4_id: "", google_ads_id: "", gtm_id: "" });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/api/settings/pixels");
        setVals({
          fb_pixel_id: data.fb_pixel_id || "",
          ga4_id: data.ga4_id || "",
          google_ads_id: data.google_ads_id || "",
          gtm_id: data.gtm_id || "",
        });
      } catch (_) { /* keep defaults */ }
      finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/api/admin/settings/pixels", vals);
      setVals({
        fb_pixel_id: data.fb_pixel_id || "",
        ga4_id: data.ga4_id || "",
        google_ads_id: data.google_ads_id || "",
        gtm_id: data.gtm_id || "",
      });
      toast.success("Pixels saved — refresh any open landing tabs to load.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div style={{ marginBottom: 28 }} data-testid="pixels-panel">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 10 }}>
        <h2 style={{ fontFamily: "var(--font-display)", fontSize: 20, color: "var(--white)", margin: 0, display: "inline-flex", alignItems: "center", gap: 8 }}>
          <Target size={16} style={{ color: "var(--gold)" }} />
          Tracking Pixels
        </h2>
        <div style={{ fontSize: 11, color: "var(--muted)" }}>
          Each pixel is independent — leave a field blank to disable it.
        </div>
      </div>

      <div className="vbs-card" style={{ padding: 18 }}>
        {loading ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--muted)", fontSize: 12 }}>
            <Loader2 size={12} className="animate-spin" /> Loading…
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px,1fr))", gap: 14 }}>
            {FIELDS.map((f) => (
              <label key={f.key} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ display: "inline-flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: 1.5, color: "var(--gold-dim)", textTransform: "uppercase" }}>{f.label}</span>
                  <a href={f.docs} target="_blank" rel="noreferrer" style={{ fontSize: 10, color: "var(--muted)", display: "inline-flex", alignItems: "center", gap: 3 }}>docs <ExternalLink size={9} /></a>
                </span>
                <input
                  data-testid={`pixel-input-${f.key}`}
                  className="vbs-input"
                  value={vals[f.key]}
                  onChange={(e) => setVals((v) => ({ ...v, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                  style={{ fontFamily: "var(--font-mono)" }}
                />
                <span style={{ fontSize: 11, color: "var(--muted)" }}>{f.help}</span>
              </label>
            ))}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
          <button
            data-testid="pixels-save"
            onClick={save}
            disabled={busy || loading}
            className="vbs-btn vbs-btn-gold"
            style={{ padding: "10px 18px" }}
          >
            {busy ? <><Loader2 size={12} className="animate-spin" /> Saving…</> : <><Save size={12} /> Save pixels</>}
          </button>
        </div>
      </div>
    </div>
  );
}
